import type { Product } from '@/lib/types/Product'
import type { PurchaseData } from '@/lib/types/PurchaseData'
import type { User } from '@/lib/types/User'
import type { WorkState } from '@/lib/types/WorkState'
import { computed, ref, watch } from 'vue'

export function usePurchaseState(
  props: {
    product: Product
    user: User
    initialWhen: 'now' | 'next'
    loginLink: string
    customerPortalLink: string
    nextOrderLink: string
    purchaseState: WorkState
    initialSellingPlan?: string
    initialQuantity: number
    initialVariantId?: string
  },
  emit: {
    (e: 'interaction'): void
    (e: 'variantChange', variantId: string): void
    (e: 'purchase', purchaseData: PurchaseData): void
  },
) {
  const selectedVariantId = ref(
    props.initialVariantId
      ? props.initialVariantId
      : props.product.selectedVariantId.toString(),
  )
  const quantity = ref(props.initialQuantity)
  const when = ref(props.initialWhen)
  const url = unescape(props.product.url)

  const selectedVariant = computed(
    () => props.product.variants[selectedVariantId.value],
  )

  const getInitialSellingPlan = () => {
    if (props.initialSellingPlan) {
      return props.initialSellingPlan.toString()
    }

    if (
      props.product.sellingPlanGroups.length === 0 ||
      selectedVariant.value.sellingPlanAllocations.length === 0
    ) {
      return null
    }

    const isStarterKit = props.product.starterKitSubscriptionConfig !== null

    if (!isStarterKit && !props.product.requiresSellingPlan) {
      return null
    }

    let sellingPlanId =
      selectedVariant.value.sellingPlanAllocations[0].sellingPlan.id.toString()
    if (typeof props.product.defaultFrequency === 'string') {
      const suggestedFrequency = parseInt(
        props.product.defaultFrequency.split(' ')[0],
      )
      const sellingPlanAllocation =
        selectedVariant.value.sellingPlanAllocations.find((spa) => {
          if (
            /every (1 )?month/i.test(spa.sellingPlan.name) &&
            suggestedFrequency === 1
          )
            return true
          if (/2 month/i.test(spa.sellingPlan.name) && suggestedFrequency === 2)
            return true
          if (/3 month/i.test(spa.sellingPlan.name) && suggestedFrequency === 3)
            return true
          if (/4 month/i.test(spa.sellingPlan.name) && suggestedFrequency === 4)
            return true
          if (/5 month/i.test(spa.sellingPlan.name) && suggestedFrequency === 5)
            return true
          if (/6 month/i.test(spa.sellingPlan.name) && suggestedFrequency === 6)
            return true
          return false
        })
      if (sellingPlanAllocation) {
        sellingPlanId = sellingPlanAllocation.sellingPlan.id.toString()
      }
    }
    return sellingPlanId
  }

  const selectedSellingPlanId = ref(getInitialSellingPlan())
  const selectedSellingPlan = computed(() => {
    return selectedVariant.value.sellingPlanAllocations.find(
      (spa) => spa.sellingPlan.id.toString() === selectedSellingPlanId.value,
    )
  })

  const isSubscriptionProduct = computed(
    () => selectedVariant.value.sellingPlanAllocations.length > 0,
  )

  const price = computed(() => parseInt(selectedVariant.value.price))
  const subscribePrice = computed(() => {
    if (selectedSellingPlan.value) {
      return selectedSellingPlan.value.price
    } else {
      return selectedVariant.value.sellingPlanAllocations &&
        selectedVariant.value.sellingPlanAllocations[0]
        ? selectedVariant.value.sellingPlanAllocations[0].price
        : null
    }
  })

  const subscribeAndSave = computed(() =>
    selectedVariant.value.sellingPlanAllocations.reduce((accum, spa) => {
      return accum || spa.price < spa.compareAtPrice
    }, false),
  )

  const savePercent = computed(() =>
    selectedVariant.value.sellingPlanAllocations.reduce((accum, spa) => {
      return Math.max(accum, spa.sellingPlan.price_adjustments[0].value)
    }, 0),
  )

  const pricePer = computed(() => {
    const comparisonValue = 100
    if (props.product.size && props.product.size.value !== null) {
      return {
        unit: `${comparisonValue} ${props.product.size.unit}`,
        price: price.value / (props.product.size.value / comparisonValue),
        subscribePrice: subscribePrice.value
          ? subscribePrice.value / (props.product.size.value / comparisonValue)
          : null,
      }
    } else {
      return null
    }
  })

  const subscriptionToThisProduct = computed(() => {
    if (props.user.loadState === 'LOADING') {
      return null
    } else if (!isSubscriptionProduct.value) {
      return null
    } else if (props.user.subscriptions.length < 1) {
      return null
    } else {
      const subscription = props.user.subscriptions.find(
        (sub) =>
          selectedVariantId.value.toString() ===
          sub.external_variant_id.ecommerce,
      )
      return subscription ? subscription : null
    }
  })

  const hasVariants = Object.keys(props.product.variants).length > 1

  watch(when, () => {
    emit('interaction')
  })

  watch(quantity, () => {
    emit('interaction')
  })

  watch(selectedVariantId, () => {
    emit('interaction')
    emit('variantChange', selectedVariantId.value)
  })

  const emitPurchaseEvent = (overrides = {}) => {
    const nextOrderInfo: {
      customerAddressId?: string
      customerNextChargeDate?: string
    } = {}
    if (when.value === 'next') {
      nextOrderInfo.customerAddressId =
        props.user.subscriptions[0].address_id.toString()
      nextOrderInfo.customerNextChargeDate = nextOrderDate.value as string
    }
    const purchaseData: PurchaseData = {
      id: props.product.id,
      selectedVariantId: selectedVariantId.value,
      selectedSellingPlanId: selectedSellingPlanId.value || undefined,
      quantity: quantity.value,
      when: when.value,
      ...overrides,
      ...nextOrderInfo,
    }
    emit('purchase', purchaseData)
  }

  const nextOrderDate = computed(() => {
    return props.user.subscriptions.reduce(
      (accum, sub) => {
        if (accum === null) {
          if (sub.next_charge_scheduled_at) {
            return sub.next_charge_scheduled_at
          } else {
            return null
          }
        } else {
          if (sub.next_charge_scheduled_at) {
            if (new Date(sub.next_charge_scheduled_at) < new Date(accum)) {
              return sub.next_charge_scheduled_at
            } else {
              return accum
            }
          } else {
            return accum
          }
        }
      },
      null as string | null,
    )
  })

  watch(
    () => props.user.loadState,
    () => {
      when.value = nextOrderDate.value ? 'next' : 'now'
    },
    { immediate: true },
  )

  return {
    selectedVariantId,
    selectedVariant,
    selectedSellingPlanId,
    selectedSellingPlan,
    hasVariants,
    price,
    subscribeAndSave,
    subscribePrice,
    pricePer,
    savePercent,
    url,
    isSubscriptionProduct,
    subscriptionToThisProduct,
    when,
    quantity,
    emitPurchaseEvent,
    nextOrderDate,
  }
}
